import { parse } from 'hub-http/helpers/params';
import { getQueryParams, stringifyQueryParams } from 'pricing-pages-lib/utils/queryParamUtils';
import { useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import throttle from 'transmute/throttle';
import { updateQueryParamTerm } from '../../actions/QueryParamTermActions';
import { useAppDispatch } from '../../reducers';
import { queryParamTermSelector } from '../../selectors/QueryParamTermSelectors';
// Add type guard
function isValidQueryValue(value) {
  return value !== undefined && value !== null;
}
export const setQueryParams = updatedParams => {
  const filteredParams = Object.fromEntries(Object.entries(updatedParams).filter(([__, value]) => isValidQueryValue(value)));
  const newQueryString = stringifyQueryParams(Object.assign({}, getQueryParams(), filteredParams));
  const newUrl = `${window.location.pathname}?${newQueryString}`;
  window.history.replaceState(window.history.state, '', newUrl);
};
export const throttledSetQueryParams = throttle(300, setQueryParams);
export const useQueryParamsEffect = params => {
  useEffect(() => {
    const hasValidParams = Object.values(params).some(isValidQueryValue);
    if (hasValidParams) {
      throttledSetQueryParams(params);
    }
  }, [params]); // Use params directly since setQueryParams is throttled
};
export const useQueryParamTerm = () => {
  const dispatch = useAppDispatch();
  const queryParamTerm = useSelector(queryParamTermSelector);
  const setQueryParamTerm = updatedQueryParamTerm => {
    if (!updatedQueryParamTerm) return;
    dispatch(updateQueryParamTerm(updatedQueryParamTerm));
  };
  return {
    queryParamTerm,
    setQueryParamTerm
  };
};
export const _getQueryParamFromLocation = ({
  paramKey,
  location
}) => {
  let queryParam;
  if (location) {
    const query = parse(location.search.slice(1));
    if (query) {
      if (query[paramKey]) {
        queryParam = decodeURIComponent(query[paramKey]);
      } else if (query[paramKey.toLowerCase()]) {
        // shortlinks will lowercase passthrough query params so we have to check both
        queryParam = decodeURIComponent(query[paramKey.toLowerCase()]);
      }
    }
  }
  return queryParam;
};
export const useQueryParamFromLocation = paramKey => {
  return _getQueryParamFromLocation({
    paramKey,
    location: useLocation()
  });
};
export const useQuery = () => {
  const {
    search
  } = useLocation();
  return useMemo(() => {
    return parse(search.slice(1));
  }, [search]);
};
export const useCurrentParam = paramName => {
  const query = useQuery();
  return query[paramName] || null;
};
export const useSetQueryParams = () => {
  const history = useHistory();
  const location = useLocation();
  return updatedParams => {
    const filteredParams = Object.fromEntries(Object.entries(updatedParams).filter(([__, value]) => isValidQueryValue(value)));
    const newQueryString = stringifyQueryParams(Object.assign({}, getQueryParams(), filteredParams));
    history.replace({
      pathname: location.pathname,
      search: newQueryString
    });
  };
};